import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote, Heart, Users, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

interface Testimonial {
    id: string;
    name: string;
    rating: number;
    comment: string;
    is_active: boolean;
    created_at: string;
    image?: string;
    sort_order?: number;
    updated_at?: string;
}

const Testimonials = () => {
    const { t, currentLanguage } = useLanguage();
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState(true);
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const response = await axios.get(`/api/v1/testimonials?featured=true&limit=6&lang=${currentLanguage}`);
                setTestimonials(response.data);
            } catch (error) {
                console.error('Failed to fetch testimonials:', error);
                // Fallback testimonials
                setTestimonials([
                    {
                        id: '1',
                        name: 'Sarah Johnson',
                        rating: 5,
                        comment: 'The best dining and entertainment experience in Goma! The all-you-can-eat buffet was incredible, and the 24/7 service is amazing.',
                        is_active: true,
                        created_at: '2024-01-15',
                    },
                    {
                        id: '2',
                        name: 'Michael Chen',
                        rating: 5,
                        comment: 'Amazing flavors and authentic taste. The butter chicken is to die for! Highly recommend for anyone looking for quality Indian cuisine.',
                        is_active: true,
                        created_at: '2024-01-10',
                    },
                    {
                        id: '3',
                        name: 'Emma Rodriguez',
                        rating: 4,
                        comment: 'Beautiful ambiance and delicious food. The naan bread is freshly made and the curries are perfectly spiced.',
                        is_active: true,
                        created_at: '2024-01-08',
                    },
                    {
                        id: '4',
                        name: 'David Thompson',
                        rating: 5,
                        comment: 'Outstanding dining experience. The staff is friendly and the food quality is consistently excellent.',
                        is_active: true,
                        created_at: '2024-01-05',
                    },
                    {
                        id: '5',
                        name: 'Lisa Wang',
                        rating: 5,
                        comment: 'The biryani is absolutely divine! Authentic flavors that transport you straight to India. A must-visit restaurant.',
                        is_active: true,
                        created_at: '2024-01-03',
                    },
                    {
                        id: '6',
                        name: 'James Wilson',
                        rating: 4,
                        comment: 'Great atmosphere and wonderful food! The premium bar selection is excellent and the gaming area is so much fun. Will definitely return!',
                        is_active: true,
                        created_at: '2024-01-01',
                    },
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, [currentLanguage]);

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`w-4 h-4 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                    }`}
            />
        ));
    };

    if (loading) {
        return (
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
                <div className="container mx-auto max-w-7xl container-padding">
                    <div className="text-center">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full mx-auto"
                        />
                        <p className="mt-4 text-gray-600 dark:text-gray-300">Loading testimonials...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
            {/* Background Decorations */}
            <div className="absolute inset-0">
                <div className="absolute top-32 left-20 w-64 h-64 bg-primary-200 dark:bg-primary-800 rounded-full opacity-5 blur-xl"></div>
                <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent-200 dark:bg-accent-800 rounded-full opacity-5 blur-xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary-200 dark:bg-secondary-600 rounded-full opacity-3 blur-xl"></div>
            </div>

            <div className="relative container mx-auto max-w-7xl container-padding">
                {/* Enhanced Header Section */}
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={inView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="inline-flex items-center space-x-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-4 py-2 rounded-full text-sm font-medium mb-6"
                    >
                        <Heart className="w-4 h-4" />
                        <span>{t('testimonials.customerStories')}</span>
                    </motion.div>

                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight">
                        {t('testimonials.title')} <span className="text-gradient">{t('testimonials.subtitle')}</span>
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
                        {t('testimonials.description')}
                    </p>

                    {/* Trust Metrics */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="flex justify-center items-center space-x-8 mt-12 flex-wrap gap-4"
                    >
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <Star className="w-5 h-5 text-yellow-500" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('testimonials.stats.averageRating')}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <Users className="w-5 h-5 text-primary-600" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('testimonials.stats.totalReviews')}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg">
                            <TrendingUp className="w-5 h-5 text-accent-600" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('testimonials.stats.satisfiedCustomers')}</span>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Enhanced Testimonials Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="group"
                        >
                            <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-gray-100 dark:border-gray-700 overflow-hidden">
                                {/* Background Pattern */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/20 dark:to-accent-900/20 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>

                                {/* Quote Icon with Animation */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                                    transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                                    className="relative z-10 mb-6"
                                >
                                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                                        <Quote className="w-8 h-8 text-white" />
                                        <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                                    </div>
                                </motion.div>

                                {/* Rating Section */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-1">
                                        {renderStars(testimonial.rating)}
                                    </div>
                                    <div className="bg-gradient-to-r from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 px-3 py-1 rounded-full">
                                        <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
                                            {testimonial.rating}/5
                                        </span>
                                    </div>
                                </div>

                                {/* Comment */}
                                <div className="mb-8">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base italic relative">
                                        <span className="text-primary-400 text-3xl absolute -top-2 -left-2">"</span>
                                        <span className="relative z-10 pl-4">{testimonial.comment}</span>
                                        <span className="text-primary-400 text-3xl absolute -bottom-6 -right-2">"</span>
                                    </p>
                                </div>

                                {/* Customer Info */}
                                <div className="flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center space-x-4">
                                        {/* Avatar */}
                                        <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full flex items-center justify-center shadow-lg">
                                            <span className="text-white font-bold text-lg">
                                                {testimonial.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 dark:text-white text-base">
                                                {testimonial.name}
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {new Date(testimonial.created_at).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    {testimonial.is_active && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={inView ? { opacity: 1 } : {}}
                                            transition={{ duration: 0.3, delay: index * 0.1 + 0.5 }}
                                            className="bg-gradient-to-r from-accent-400 to-secondary-400 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
                                        >
                                            ⭐ Featured
                                        </motion.div>
                                    )}
                                </div>

                                {/* Decorative Element */}
                                <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full opacity-60"></div>

                                {/* Hover Glow Effect */}
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Enhanced Overall Rating Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mt-24"
                >
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-12 shadow-2xl border border-gray-100 dark:border-gray-700 relative overflow-hidden max-w-4xl mx-auto">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-500 to-accent-500"></div>
                            <div className="absolute top-0 left-0 w-full h-full" style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                            }}></div>
                        </div>

                        <div className="relative text-center">
                            {/* Badge */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={inView ? { opacity: 1 } : {}}
                                transition={{ duration: 0.4, delay: 0.6 }}
                                className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 text-primary-700 dark:text-primary-300 px-4 py-2 rounded-full text-sm font-medium mb-6"
                            >
                                <Star className="w-4 h-4" />
                                <span>{t('testimonials.overallRating')}</span>
                            </motion.div>

                            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-gray-900 dark:text-white">
                                {t('testimonials.overall.title')}
                            </h3>

                            {/* Large Rating Display */}
                            <div className="flex items-center justify-center mb-8">
                                <div className="flex items-center space-x-2 mr-6">
                                    {renderStars(4)}
                                </div>
                                <motion.span
                                    initial={{ opacity: 0 }}
                                    animate={inView ? { opacity: 1 } : {}}
                                    transition={{ duration: 0.5, delay: 0.8 }}
                                    className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent"
                                >
                                    4.3
                                </motion.span>
                                <span className="text-2xl text-gray-500 dark:text-gray-400 ml-2">/5</span>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                                {[
                                    { label: t('testimonials.stats.totalReviewsLabel'), value: testimonials.length.toString(), icon: Users },
                                    { label: t('testimonials.stats.fiveStarReviews'), value: "85%", icon: Star },
                                    { label: t('testimonials.stats.recommendUs'), value: "95%", icon: Heart }
                                ].map((stat, index) => (
                                    <motion.div
                                        key={stat.label}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={inView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.4, delay: 1 + index * 0.1 }}
                                        className="text-center"
                                    >
                                        <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                            <stat.icon className="w-8 h-8 text-primary-600" />
                                        </div>
                                        <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                            {stat.value}
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                            {stat.label}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                                {t('testimonials.overall.description').replace('{count}', testimonials.length.toString())}
                            </p>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-8 right-8 w-4 h-4 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full opacity-60"></div>
                        <div className="absolute bottom-8 left-8 w-6 h-6 bg-gradient-to-br from-accent-400 to-secondary-400 rounded-full opacity-40"></div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Testimonials;
